import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus, Check, Loader2, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/button';
import { usePortalCart } from '../../context/PortalCartContext';

/**
 * Add-to-cart control with built-in MOQ enforcement.
 *
 * Two visual modes:
 *   variant="card"  → compact stepper that fits inside a catalog tile
 *   variant="row"   → inline horizontal stepper for the list view
 *
 * Behaviour:
 *   - For products not yet in the cart we show a single "Add (×N)" CTA where
 *     N defaults to the MOQ; clicking adds exactly N units.
 *   - Once a product is in the cart we show a +/- stepper bound to the
 *     server qty; the - button is disabled when qty == MOQ to make the MOQ
 *     constraint discoverable rather than punitive.
 *   - Out-of-stock products show an inert disabled badge instead.
 *
 * Server is the source of truth: every interaction calls the API and
 * the cart context propagates the new totals to the layout badge.
 */
export const AddToCart = ({ product, variant = 'card', disabled = false }) => {
    const { t } = useTranslation('portal');
    const { cart, moq, step, addItem, updateItem, removeItem } = usePortalCart();
    const [busy, setBusy] = useState(false);

    const line = useMemo(
        () => cart?.items?.find((it) => it.product_id === product.id),
        [cart, product.id],
    );
    const inCartQty = line?.qty || 0;

    if (!product.in_stock) {
        return (
            <Button
                disabled
                size={variant === 'card' ? 'sm' : 'sm'}
                data-testid={`add-to-cart-disabled-${product.sku}`}
                className="w-full bg-gray-100 text-gray-400 cursor-not-allowed rounded-full"
            >
                {t('add_to_cart.out_of_stock')}
            </Button>
        );
    }

    const guard = async (fn) => {
        if (busy) return;
        setBusy(true);
        try { await fn(); }
        finally { setBusy(false); }
    };

    // --- Not yet in cart: single "Add ×MOQ" CTA --------------------------
    if (inCartQty === 0) {
        return (
            <Button
                size="sm"
                disabled={disabled || busy}
                onClick={() => guard(() => addItem(product.id, moq))}
                data-testid={`add-to-cart-${product.sku}`}
                className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full"
            >
                {busy ? (
                    <Loader2 size={14} className="animate-spin mr-1.5" />
                ) : (
                    <ShoppingCart size={14} className="mr-1.5" />
                )}
                {t('add_to_cart.add_n', { count: moq })}
            </Button>
        );
    }

    // --- Already in cart: qty stepper + remove ---------------------------
    const dec = () => {
        if (inCartQty <= moq) {
            // At MOQ floor — clicking minus removes the line entirely so the
            // user can re-add or skip the product. Communicates the rule
            // visually (button gets stricter as it nears MOQ).
            return guard(() => removeItem(product.id));
        }
        return guard(() => updateItem(product.id, inCartQty - step));
    };
    const inc = () => guard(() => updateItem(product.id, inCartQty + step));

    return (
        <div
            data-testid={`cart-stepper-${product.sku}`}
            className="flex items-center justify-between gap-2 bg-[#FBF8F6] border border-[#EFE5E1] rounded-full px-1 py-1"
        >
            <button
                onClick={dec}
                disabled={busy}
                aria-label={t('add_to_cart.decrease_aria')}
                data-testid={`cart-dec-${product.sku}`}
                className="w-7 h-7 rounded-full bg-white border border-[#EFE5E1] flex items-center justify-center text-gray-600 hover:bg-[#F5EEEA] disabled:opacity-50"
            >
                <Minus size={13} />
            </button>
            <span className="text-sm font-semibold text-[#1A1A1A] tabular-nums" data-testid={`cart-qty-${product.sku}`}>
                {busy ? '…' : inCartQty}
            </span>
            <button
                onClick={inc}
                disabled={busy}
                aria-label={t('add_to_cart.increase_aria')}
                data-testid={`cart-inc-${product.sku}`}
                className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center text-white hover:bg-[#333] disabled:opacity-50"
            >
                <Plus size={13} />
            </button>
            <Check size={14} className="text-emerald-600 mr-1" aria-hidden />
        </div>
    );
};

export default AddToCart;
